import { useEffect, useState, type KeyboardEvent } from 'react';
import { signInUrl } from '../api.ts';
import './landing.css';

const TITLE = 'Computational Pathology & Digital Medicine';

/** Sets the tab title; the function it returns puts the one before back. */
export function swapTitle(title: string): () => void {
  const before = document.title;
  document.title = title;
  return () => {
    document.title = before;
  };
}

/** The two answers under the sign-in button. The password is typed on the Microsoft page, not here. */
const TIPS = {
  invited:
    'Choose Sign in, enter the address you were invited at, then “Forgot password?”: you get a code by email, then set your password (the one from the old site is fine). If it finds no account, choose “No account? Create one” instead.',
  forgot: 'Choose Sign in, enter your email, then “Forgot password?” on the next page.',
};
type Tip = keyof typeof TIPS;

/** The card says why it asks to sign in, by the page asked for (old destContext): "Sign in to …". */
const WHY: Record<string, string> = {
  '/suggest': 'suggest a topic',
  '/my-applications': 'see your applications',
  '/inbox': 'open the applications inbox',
  '/apply': 'apply for a topic',
  '/comments': 'open the comments log',
  '/gantt': 'open the Gantt',
};

/** On the pages a newcomer may open, what creating an account does (old destContext hints). */
const AS_APPLICANT = 'that registers you as an applicant, so you can apply for open topics.';
const NEWCOMER: Record<string, string> = {
  '/suggest':
    'that also registers your interest in joining. You can suggest a topic straight away; an administrator reviews it and can accept you as a student for full access.',
  '/my-applications': AS_APPLICANT,
  '/apply': AS_APPLICANT,
};

/**
 * What a visitor who is not signed in sees: the old pathonline.net home page, full screen. `asked`: the page
 * the visitor asked for (Layout): this address, or at "/" the page of an old "?dest=?view=…" link.
 */
export function Landing({ asked }: { asked: string }) {
  const [tip, setTip] = useState<Tip | null>(null);
  const why = WHY[asked];
  const newcomer = NEWCOMER[asked];
  useEffect(() => swapTitle(TITLE), []);
  // One answer open at a time; its own link closes it again. No href, as old: hovering shows no URL.
  const toggle = (t: Tip) => () => setTip((open) => (open === t ? null : t));
  const tipLink = (t: Tip) => ({
    role: 'button',
    tabIndex: 0,
    'aria-expanded': tip === t,
    onClick: toggle(t),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      toggle(t)();
    },
  });
  const suggest = signInUrl('/suggest');

  return (
    <div className="lp">
      <header className="nav">
        <div className="nav-in">
          <a className="brand" href="/">
            <span className="dot">🧬</span>
            <span>Computational Pathology<small>&amp; Digital Medicine</small></span>
          </a>
          <nav className="links">
            <span className="prog">Programme <a href="/research-programme/" hrefLang="en">EN</a><span className="bar">|</span><a href="/research-programme/lt/" hrefLang="lt" lang="lt">LT</a></span>
            <a href="/apply">Open topics</a>
            <a href="/docs/" target="_blank" rel="noopener">Guides</a>
            <a href={suggest}>Suggest a topic</a>
            <a href="#signin">Sign in</a>
            <a className="cta" href="/apply">Apply →</a>
          </nav>
        </div>
      </header>

      <section className="hero">
        <div className="hero-in">
          <div className="hero-copy">
            <div className="eyebrow">Digital Medicine Research Hub</div>
            <h1>Computational pathology for kidney &amp; cancer diagnostics</h1>
            <p className="lede">A research programme in computational pathology and digital medicine at Vilnius University.</p>
            <div className="hero-cta">
              <a className="hbtn hbtn-primary" href="/research-programme/">Explore the programme →</a>
              <a className="hbtn hbtn-ghost" href="/apply">Browse open topics</a>
            </div>
            <p className="hero-guide">
              New here? Read the{' '}
              <a href="/docs/mentors-students.html" target="_blank" rel="noopener">student &amp; mentor quick-start guide ↗</a>
              {' '}— no sign-in needed.
            </p>
            <div className="facts">
              <div><b>Open</b> thesis &amp; project topics</div>
              <div><b>BSc · MSc · PhD</b> levels</div>
              <div><b>Digital</b> pathology &amp; medicine</div>
            </div>
          </div>

          <div className="card" id="signin">
            <div className="hd">
              <h2 className="mode" style={{ color: '#fff' }}>{why ? 'Sign in to continue' : 'Sign in'}</h2>
              {why ? (
                <p className="dest-why">
                  Sign in to <b>{why}</b>
                </p>
              ) : (
                <p>Members &amp; invited collaborators</p>
              )}
            </div>
            {newcomer && (
              <div className="bd" style={{ paddingBottom: 0 }}>
                <p className="dest-hint">
                  New here? Choose Sign in, then <b>“No account? Create one”</b> — {newcomer}
                </p>
              </div>
            )}
            <div className="bd">
              <p className="hint" style={{ marginTop: 0 }}>Sign in with your email and password, or with Google.</p>
              {/* Back to the page that was asked for (an emailed link); "/" then routes by role. */}
              <a className="btn btn-primary" href={signInUrl()}>Sign in</a>
              <div className="rowlinks">
                <a {...tipLink('invited')}>Invited but never signed in?</a>
                <a {...tipLink('forgot')}>Forgot your password?</a>
              </div>
              {tip && <p className="hint">{TIPS[tip]}</p>}
            </div>
            <div className="foot-note">
              {/* Not old: the old line was "You must be invited…", which read as a closed door to applicants. */}
              Members are invited by an administrator.
              {!newcomer && ' Applying for a topic or suggesting one? Choose Sign in, then “No account? Create one”.'} &nbsp;·&nbsp;{' '}
              <a href="/docs/mentors-students.html" target="_blank" rel="noopener" style={{ color: 'inherit', textDecoration: 'underline' }}>How to get started</a>
            </div>
          </div>
        </div>
      </section>

      <footer className="pg">
        <div>Computational Pathology &amp; Digital Medicine · Digital Medicine Research Hub</div>
        <div>
          Research programme <a href="/research-programme/" hrefLang="en">EN</a>&thinsp;|&thinsp;<a href="/research-programme/lt/" hrefLang="lt" lang="lt">LT</a> &nbsp;·&nbsp;{' '}
          <a href="/apply">Open topics ↗</a> &nbsp;·&nbsp;{' '}
          <a href="/docs/" target="_blank" rel="noopener">Guides ↗</a> &nbsp;·&nbsp;{' '}
          <a href={suggest}>Suggest a topic ↗</a>
        </div>
      </footer>
    </div>
  );
}
